// Three.js atmospheric scene — backdrop that re-skins per room atmosphere preset.
// No actual gameplay geometry yet (player/NPCs/mobs render as DOM tags in the room
// panel); this is mood lighting + ground + ambient floats.

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { AtmospherePreset } from "../shared/protocol.js";

interface AtmosphereSpec {
  sky:    number;       // background color
  fog:    number;       // fog color
  fogDensity: number;
  light:  number;       // directional light color
  lightIntensity: number;
  ground: number;       // ground plane color
  ambient: number;      // ambient light color
  ambientIntensity: number;
  /** Optional ambient particle hex color (snow/ash/dust/embers). 0 disables. */
  particleColor: number;
  particleCount: number;
}

const PRESETS: Record<AtmospherePreset, AtmosphereSpec> = {
  village:     { sky: 0x394050, fog: 0x394050, fogDensity: 0.025, light: 0xffe6c0, lightIntensity: 1.1, ground: 0x4a4030, ambient: 0x4d5570, ambientIntensity: 0.6, particleColor: 0xddccaa, particleCount: 60 },
  forest:      { sky: 0x10180e, fog: 0x10180e, fogDensity: 0.07,  light: 0x6a8050, lightIntensity: 0.7, ground: 0x182010, ambient: 0x182810, ambientIntensity: 0.5, particleColor: 0x224a22, particleCount: 80 },
  interior:    { sky: 0x1a1208, fog: 0x1a1208, fogDensity: 0.05,  light: 0xffaa55, lightIntensity: 1.4, ground: 0x382010, ambient: 0x402810, ambientIntensity: 0.7, particleColor: 0xffaa44, particleCount: 30 },
  tavern:      { sky: 0x261810, fog: 0x261810, fogDensity: 0.04,  light: 0xff9c44, lightIntensity: 1.5, ground: 0x4a3018, ambient: 0x4a2820, ambientIntensity: 0.8, particleColor: 0x884422, particleCount: 100 },
  cemetery:    { sky: 0x1a1a26, fog: 0x1a1a26, fogDensity: 0.05,  light: 0x9090b0, lightIntensity: 0.8, ground: 0x2a2a35, ambient: 0x202030, ambientIntensity: 0.6, particleColor: 0x6a6a90, particleCount: 50 },
  undead:      { sky: 0x0a1820, fog: 0x0a1820, fogDensity: 0.09,  light: 0x70b0c0, lightIntensity: 0.6, ground: 0x152030, ambient: 0x153040, ambientIntensity: 0.7, particleColor: 0x80e0ff, particleCount: 120 },
  mountain:    { sky: 0x4a5868, fog: 0x4a5868, fogDensity: 0.03,  light: 0xe8eef8, lightIntensity: 1.2, ground: 0x5a5048, ambient: 0x4a5868, ambientIntensity: 0.6, particleColor: 0xffffff, particleCount: 80 },
  alpine:      { sky: 0x6a7a8a, fog: 0x6a7a8a, fogDensity: 0.04,  light: 0xffffff, lightIntensity: 1.3, ground: 0x8a8a80, ambient: 0x6a7a8a, ambientIntensity: 0.7, particleColor: 0xffffff, particleCount: 150 },
  cave:        { sky: 0x0a0a10, fog: 0x0a0a10, fogDensity: 0.1,   light: 0x8844aa, lightIntensity: 0.5, ground: 0x1a1820, ambient: 0x0a0a15, ambientIntensity: 0.3, particleColor: 0x6a4488, particleCount: 40 },
  dungeon:     { sky: 0x0e0a0a, fog: 0x0e0a0a, fogDensity: 0.11,  light: 0x883322, lightIntensity: 0.4, ground: 0x201010, ambient: 0x180a0a, ambientIntensity: 0.3, particleColor: 0x882222, particleCount: 30 },
  ruins:       { sky: 0x302820, fog: 0x302820, fogDensity: 0.06,  light: 0xaa8866, lightIntensity: 0.8, ground: 0x382c20, ambient: 0x30281f, ambientIntensity: 0.5, particleColor: 0x998866, particleCount: 70 },
  river:       { sky: 0x2a3848, fog: 0x2a3848, fogDensity: 0.04,  light: 0xaaccdd, lightIntensity: 1.0, ground: 0x304050, ambient: 0x2a3848, ambientIntensity: 0.6, particleColor: 0x6ab0ff, particleCount: 100 },
  city:        { sky: 0x30303a, fog: 0x30303a, fogDensity: 0.035, light: 0xfff5c0, lightIntensity: 1.2, ground: 0x3a3a40, ambient: 0x303038, ambientIntensity: 0.65, particleColor: 0xd0c088, particleCount: 70 },
  cathedral:   { sky: 0x28241c, fog: 0x28241c, fogDensity: 0.04,  light: 0xffddaa, lightIntensity: 1.3, ground: 0x38322a, ambient: 0x2a2620, ambientIntensity: 0.7, particleColor: 0xffcc80, particleCount: 50 },
  coast:       { sky: 0x6a8ea8, fog: 0x6a8ea8, fogDensity: 0.03,  light: 0xffebd0, lightIntensity: 1.2, ground: 0x8a7a5a, ambient: 0x6a8ea8, ambientIntensity: 0.7, particleColor: 0xffffff, particleCount: 70 },
  lake:        { sky: 0x3a4a5a, fog: 0x3a4a5a, fogDensity: 0.05,  light: 0xc0e0ff, lightIntensity: 1.0, ground: 0x2a3a48, ambient: 0x3a4a5a, ambientIntensity: 0.65, particleColor: 0x88ccff, particleCount: 90 },
  swamp:       { sky: 0x1a2818, fog: 0x1a2818, fogDensity: 0.08,  light: 0x889064, lightIntensity: 0.6, ground: 0x223018, ambient: 0x182818, ambientIntensity: 0.5, particleColor: 0x668844, particleCount: 120 },
  battlefield: { sky: 0x3a2818, fog: 0x3a2818, fogDensity: 0.07,  light: 0xff6633, lightIntensity: 1.0, ground: 0x3a2820, ambient: 0x321e14, ambientIntensity: 0.6, particleColor: 0xcc3322, particleCount: 150 },
  demonic:     { sky: 0x2a0a0a, fog: 0x2a0a0a, fogDensity: 0.08,  light: 0xff3322, lightIntensity: 1.0, ground: 0x200a0a, ambient: 0x2a0a0a, ambientIntensity: 0.5, particleColor: 0xff2200, particleCount: 180 },
  celestial:   { sky: 0xd8e8f8, fog: 0xd8e8f8, fogDensity: 0.02,  light: 0xffffff, lightIntensity: 1.6, ground: 0xe8ecf4, ambient: 0xd0ddeb, ambientIntensity: 1.0, particleColor: 0xffffff, particleCount: 200 },
  void:        { sky: 0x050508, fog: 0x050508, fogDensity: 0.1,   light: 0x6644ff, lightIntensity: 0.6, ground: 0x0a0a10, ambient: 0x050508, ambientIntensity: 0.3, particleColor: 0x8866ff, particleCount: 100 },
  tower:       { sky: 0x1a1a24, fog: 0x1a1a24, fogDensity: 0.04,  light: 0xffddaa, lightIntensity: 1.1, ground: 0x2a2828, ambient: 0x1a1a24, ambientIntensity: 0.6, particleColor: 0xaaaaaa, particleCount: 60 },
  monastery:   { sky: 0x242820, fog: 0x242820, fogDensity: 0.04,  light: 0xf0d9aa, lightIntensity: 1.2, ground: 0x30322a, ambient: 0x242820, ambientIntensity: 0.7, particleColor: 0xccbb88, particleCount: 50 },
  fortress:    { sky: 0x2a2a30, fog: 0x2a2a30, fogDensity: 0.04,  light: 0xe0d8c8, lightIntensity: 1.0, ground: 0x3a3838, ambient: 0x2a2a30, ambientIntensity: 0.6, particleColor: 0xaaaaaa, particleCount: 80 },
  frontier:    { sky: 0x40382a, fog: 0x40382a, fogDensity: 0.05,  light: 0xffcc88, lightIntensity: 1.0, ground: 0x483828, ambient: 0x40382a, ambientIntensity: 0.6, particleColor: 0xccaa66, particleCount: 70 },
  market:      { sky: 0x363028, fog: 0x363028, fogDensity: 0.03,  light: 0xffe0b0, lightIntensity: 1.2, ground: 0x3a3028, ambient: 0x363028, ambientIntensity: 0.7, particleColor: 0xeeccaa, particleCount: 100 },
};

export class Scene {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private ground: THREE.Mesh;
  private ambientLight: THREE.AmbientLight;
  private keyLight: THREE.DirectionalLight;
  private particles: THREE.Points | null = null;
  private particleVel: Float32Array = new Float32Array(0);
  private currentPreset: AtmospherePreset = "village";
  private gltfLoader = new GLTFLoader();
  /** Characters keyed by actor id (mob:<n>, npc:<n>). Cleared on scene clear. */
  private actorMeshes = new Map<string, THREE.Object3D>();
  /** Model URL cache: url -> shared scene Object3D we clone per placement. */
  private modelCache = new Map<string, THREE.Object3D>();

  constructor(host: HTMLElement) {
    const w = host.clientWidth || window.innerWidth;
    const h = host.clientHeight || window.innerHeight;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(w, h, false);
    host.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 100);
    this.camera.position.set(0, 1.4, 5);
    this.camera.lookAt(0, 0, 0);

    this.ambientLight = new THREE.AmbientLight(0x444466, 0.6);
    this.scene.add(this.ambientLight);
    this.keyLight = new THREE.DirectionalLight(0xffe9b4, 1.0);
    this.keyLight.position.set(3, 5, 4);
    this.scene.add(this.keyLight);

    const groundGeo = new THREE.PlaneGeometry(40, 40);
    const groundMat = new THREE.MeshStandardMaterial({ color: 0x4a4030, roughness: 0.95 });
    this.ground = new THREE.Mesh(groundGeo, groundMat);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.y = -0.7;
    this.scene.add(this.ground);

    this.applyAtmosphere(this.currentPreset);

    window.addEventListener("resize", () => this.resize());
  }

  applyAtmosphere(preset: AtmospherePreset): void {
    if (this.currentPreset === preset && this.particles) return;
    this.currentPreset = preset;
    const a = PRESETS[preset] ?? PRESETS.village;
    this.scene.background = new THREE.Color(a.sky);
    this.scene.fog = new THREE.FogExp2(a.fog, a.fogDensity);
    this.ambientLight.color.setHex(a.ambient);
    this.ambientLight.intensity = a.ambientIntensity;
    this.keyLight.color.setHex(a.light);
    this.keyLight.intensity = a.lightIntensity;
    (this.ground.material as THREE.MeshStandardMaterial).color.setHex(a.ground);

    // Re-build particles for this preset.
    if (this.particles) {
      this.scene.remove(this.particles);
      this.particles.geometry.dispose();
      (this.particles.material as THREE.Material).dispose();
      this.particles = null;
    }
    if (a.particleCount > 0) {
      const geom = new THREE.BufferGeometry();
      const positions = new Float32Array(a.particleCount * 3);
      this.particleVel = new Float32Array(a.particleCount * 3);
      for (let i = 0; i < a.particleCount; i++) {
        positions[i*3]   = (Math.random() - 0.5) * 20;
        positions[i*3+1] = Math.random() * 6;
        positions[i*3+2] = (Math.random() - 0.5) * 20 - 2;
        this.particleVel[i*3]   = (Math.random() - 0.5) * 0.005;
        this.particleVel[i*3+1] = -(0.005 + Math.random() * 0.01);
        this.particleVel[i*3+2] = (Math.random() - 0.5) * 0.005;
      }
      geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({ color: a.particleColor, size: 0.06, transparent: true, opacity: 0.7, depthWrite: false });
      this.particles = new THREE.Points(geom, mat);
      this.scene.add(this.particles);
    }
  }

  /** Load + cache a GLTF model and place/replace an actor in the scene.
   *  The actor key is a stable string like "mob:42" or "npc:1001" — calling
   *  setActor with the same key replaces the previous mesh. Missing or failed
   *  loads silently noop (text-chip fallback is always the UI truth). */
  async setActor(actorKey: string, modelUrl: string, position: { x?: number; y?: number; z?: number } = {}): Promise<void> {
    if (!modelUrl) { this.removeActor(actorKey); return; }
    let source = this.modelCache.get(modelUrl);
    if (!source) {
      try {
        const gltf = await this.gltfLoader.loadAsync(modelUrl);
        source = gltf.scene;
        this.modelCache.set(modelUrl, source);
      } catch {
        // Asset corrupt or network dropped — keep text fallback, don't spam.
        return;
      }
    }
    this.removeActor(actorKey);
    const clone = source.clone(true);
    clone.position.set(position.x ?? 0, position.y ?? -0.6, position.z ?? 0);
    // Normalize scale: pick a max-extent so the model fits the scene.
    const box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim > 0 && maxDim !== 1.6) {
      const s = 1.6 / maxDim;
      clone.scale.setScalar(s);
    }
    this.scene.add(clone);
    this.actorMeshes.set(actorKey, clone);
  }

  removeActor(actorKey: string): void {
    const existing = this.actorMeshes.get(actorKey);
    if (!existing) return;
    this.scene.remove(existing);
    this.actorMeshes.delete(actorKey);
  }

  /** Remove every live actor — call when the player changes room so stale
   *  meshes don't linger when the next room has different actors. */
  clearActors(): void {
    for (const [, mesh] of this.actorMeshes) this.scene.remove(mesh);
    this.actorMeshes.clear();
  }

  /** Reconcile the actor roster against a set of desired (key, url) pairs.
   *  Removes actors not in the set, adds/updates the ones that are. Callers
   *  (room renderer + combat overlay) use this to keep the scene in sync
   *  without tracking individual lifecycle events. */
  async syncActors(desired: Array<{ key: string; url: string; x?: number; z?: number }>): Promise<void> {
    const desiredKeys = new Set(desired.map((d) => d.key));
    for (const key of [...this.actorMeshes.keys()]) {
      if (!desiredKeys.has(key)) this.removeActor(key);
    }
    await Promise.all(desired.map((d) => this.setActor(d.key, d.url, { x: d.x, z: d.z })));
  }

  private resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  start(): void {
    const tick = () => {
      // Slow camera bob for life
      this.camera.position.y = 1.4 + Math.sin(performance.now() * 0.0005) * 0.05;
      this.camera.lookAt(0, 0, 0);

      if (this.particles) {
        const pos = this.particles.geometry.attributes.position;
        const arr = pos.array as Float32Array;
        for (let i = 0; i < arr.length / 3; i++) {
          arr[i*3]   += this.particleVel[i*3];
          arr[i*3+1] += this.particleVel[i*3+1];
          arr[i*3+2] += this.particleVel[i*3+2];
          if (arr[i*3+1] < -0.7) {
            arr[i*3]   = (Math.random() - 0.5) * 20;
            arr[i*3+1] = 6;
            arr[i*3+2] = (Math.random() - 0.5) * 20 - 2;
          }
        }
        pos.needsUpdate = true;
      }

      this.renderer.render(this.scene, this.camera);
      requestAnimationFrame(tick);
    };
    tick();
  }
}
